import { ADMIN_API_URL } from "@/config";

export interface SystemAdmin {
	_id: string;
	email: string;
	name: string;
	role: "super_admin" | "admin" | "support";
	isActive: boolean;
	lastLogin?: string;
	createdAt?: string;
}

export interface LoginResponse {
	admin: SystemAdmin;
	accessToken: string;
	refreshToken: string;
}

export interface AuthError {
	message: string;
	status: number;
}

const API_BASE = `${ADMIN_API_URL}/auth`;

// Token storage keys
const ACCESS_TOKEN_KEY = "admin_access_token";
const REFRESH_TOKEN_KEY = "admin_refresh_token";
const ADMIN_DATA_KEY = "admin_data";

/**
 * Store tokens in localStorage
 */
export function storeTokens(accessToken: string, refreshToken: string): void {
	localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
	localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Store admin data in localStorage
 */
export function storeAdminData(admin: SystemAdmin): void {
	localStorage.setItem(ADMIN_DATA_KEY, JSON.stringify(admin));
}

/**
 * Get access token from localStorage
 */
export function getAccessToken(): string | null {
	return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Get refresh token from localStorage
 */
export function getRefreshToken(): string | null {
	return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Get stored admin data
 */
export function getStoredAdminData(): SystemAdmin | null {
	const data = localStorage.getItem(ADMIN_DATA_KEY);
	if (!data) return null;
	try {
		return JSON.parse(data) as SystemAdmin;
	} catch {
		return null;
	}
}

/**
 * Clear all auth data from localStorage
 */
export function clearAuthData(): void {
	localStorage.removeItem(ACCESS_TOKEN_KEY);
	localStorage.removeItem(REFRESH_TOKEN_KEY);
	localStorage.removeItem(ADMIN_DATA_KEY);
}

/**
 * Check if user is authenticated (has valid token)
 */
export function isAuthenticated(): boolean {
	return !!getAccessToken();
}

/**
 * Make authenticated API request
 */
async function fetchWithAuth<T>(
	endpoint: string,
	options?: RequestInit,
): Promise<T> {
	const token = getAccessToken();

	const response = await fetch(`${API_BASE}${endpoint}`, {
		...options,
		headers: {
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...options?.headers,
		},
	});

	// Handle 401 - try to refresh token
	if (response.status === 401 && endpoint !== "/login" && endpoint !== "/refresh-token") {
		const refreshed = await tryRefreshToken();
		if (refreshed) {
			// Retry the original request with new token
			const newToken = getAccessToken();
			const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
				...options,
				headers: {
					"Content-Type": "application/json",
					...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
					...options?.headers,
				},
			});
			return handleResponse<T>(retryResponse);
		}
		// Refresh failed - clear auth data
		clearAuthData();
		throw { message: "Session expired. Please login again.", status: 401 };
	}

	return handleResponse<T>(response);
}

/**
 * Handle API response
 */
async function handleResponse<T>(response: Response): Promise<T> {
	const data = await response.json();

	if (!response.ok) {
		throw {
			message: data.message || `HTTP error ${response.status}`,
			status: response.status,
		};
	}

	if (data.success === false) {
		throw {
			message: data.message || "Request failed",
			status: response.status,
		};
	}

	return data.data ?? data;
}

/**
 * Try to refresh the access token
 */
async function tryRefreshToken(): Promise<boolean> {
	const refreshToken = getRefreshToken();
	if (!refreshToken) return false;

	try {
		const response = await fetch(`${API_BASE}/refresh-token`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refreshToken }),
		});

		if (!response.ok) return false;

		const data = await response.json();
		if (data.success && data.data) {
			storeTokens(data.data.accessToken, data.data.refreshToken);
			return true;
		}
		return false;
	} catch {
		return false;
	}
}

/**
 * Login with email and password
 */
export async function login(
	email: string,
	password: string,
): Promise<LoginResponse> {
	const response = await fetch(`${API_BASE}/login`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email, password }),
	});

	const result = await handleResponse<LoginResponse>(response);

	// Store tokens and admin data
	storeTokens(result.accessToken, result.refreshToken);
	storeAdminData(result.admin);

	return result;
}

/**
 * Logout current admin
 */
export async function logout(): Promise<void> {
	try {
		await fetchWithAuth("/logout", { method: "POST" });
	} finally {
		clearAuthData();
	}
}

/**
 * Get current admin profile
 */
export async function getMe(): Promise<SystemAdmin> {
	const admin = await fetchWithAuth<SystemAdmin>("/me");
	storeAdminData(admin);
	return admin;
}

/**
 * Change password
 */
export async function changePassword(
	currentPassword: string,
	newPassword: string,
): Promise<void> {
	await fetchWithAuth("/me/password", {
		method: "PUT",
		body: JSON.stringify({ currentPassword, newPassword }),
	});
}

/**
 * Request email change — sends 6-digit OTP to the new email address.
 */
export async function requestEmailChange(
	newEmail: string,
): Promise<{ expiresInMinutes: number }> {
	return fetchWithAuth<{ expiresInMinutes: number }>("/me/email/request", {
		method: "POST",
		body: JSON.stringify({ newEmail }),
	});
}

/**
 * Verify email change with OTP. On success, the admin must log in again.
 */
export async function verifyEmailChange(
	otp: string,
): Promise<{ newEmail: string }> {
	return fetchWithAuth<{ newEmail: string }>("/me/email/verify", {
		method: "POST",
		body: JSON.stringify({ otp }),
	});
}

/**
 * List all system admins (super_admin only)
 */
export async function listAdmins(params?: {
	page?: number;
	limit?: number;
	role?: string;
	isActive?: boolean;
	search?: string;
}): Promise<{
	admins: SystemAdmin[];
	total: number;
	page: number;
	pages: number;
}> {
	const queryParams = new URLSearchParams();
	if (params?.page) queryParams.append("page", String(params.page));
	if (params?.limit) queryParams.append("limit", String(params.limit));
	if (params?.role) queryParams.append("role", params.role);
	if (typeof params?.isActive === "boolean")
		queryParams.append("isActive", String(params.isActive));
	if (params?.search) queryParams.append("search", params.search);

	const query = queryParams.toString();
	return fetchWithAuth(`/admins${query ? `?${query}` : ""}`);
}

/**
 * Create a new system admin (super_admin only)
 */
export async function createAdmin(data: {
	email: string;
	password: string;
	name: string;
	role: "super_admin" | "admin" | "support";
}): Promise<SystemAdmin> {
	return fetchWithAuth("/admins", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

/**
 * Update a system admin (super_admin only)
 */
export async function updateAdmin(
	adminId: string,
	data: {
		name?: string;
		role?: "super_admin" | "admin" | "support";
		isActive?: boolean;
	},
): Promise<SystemAdmin> {
	return fetchWithAuth(`/admins/${adminId}`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
}

/**
 * Delete a system admin (super_admin only)
 */
export async function deleteAdmin(adminId: string): Promise<void> {
	await fetchWithAuth(`/admins/${adminId}`, { method: "DELETE" });
}

/**
 * Verify token is still valid by calling /me endpoint
 */
export async function verifyAuth(): Promise<boolean> {
	try {
		await getMe();
		return true;
	} catch {
		return false;
	}
}
